import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { XCircle } from "lucide-react";

// 19/10/2026 — chiusura anticipata: stesse chiavi di MOTIVI_CHIUSURA in
// backend/routers/policies.py
export const MOTIVI_CHIUSURA = [
  { code: "vendita_veicolo", label: "Vendita del veicolo" },
  { code: "demolizione", label: "Demolizione / rottamazione" },
  { code: "furto", label: "Furto totale" },
  { code: "esportazione", label: "Esportazione all'estero" },
  { code: "altra_agenzia", label: "Passata ad altra agenzia o compagnia" },
  { code: "richiesta_cliente", label: "Recesso su richiesta del cliente" },
  { code: "decesso", label: "Decesso dell'assicurato" },
  { code: "altro", label: "Altro" },
];

const oggi = () => new Date().toISOString().slice(0, 10);

/**
 * ClosePolicyDialog — chiude (estingue) una polizza prima della scadenza.
 * La polizza resta in archivio con documenti e storia, ma esce da scadenze,
 * promemoria e conteggi.
 */
export function ClosePolicyDialog({ open, onOpenChange, policy, clientName, onClosed }) {
  const [data, setData] = useState({ closure_date: oggi(), reason_code: "", notes: "", refund_amount: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setData({ closure_date: oggi(), reason_code: "", notes: "", refund_amount: "" });
  }, [open]);

  const conferma = async () => {
    if (!data.reason_code) { toast.error("Scegli il motivo della chiusura"); return; }
    if (!data.closure_date) { toast.error("Indica la data di chiusura"); return; }
    setSaving(true);
    try {
      await axios.post(`/api/policies/${policy.policy_id}/close`, {
        closure_date: data.closure_date,
        reason_code: data.reason_code,
        notes: data.notes || null,
        refund_amount: data.refund_amount === "" ? null : parseFloat(String(data.refund_amount).replace(",", ".")),
      });
      toast.success("Polizza chiusa");
      onOpenChange(false);
      onClosed && onClosed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella chiusura della polizza");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-gray-600" />
            Chiudi Polizza
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {policy && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{policy.policy_number}</p>
              <p className="text-sm text-muted-foreground">
                {clientName} - {policy.company}{policy.license_plate ? ` - ${policy.license_plate}` : ""}
              </p>
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            La polizza viene estinta prima della scadenza: resta in archivio con i suoi documenti,
            ma non comparirà più tra scadenze, promemoria e conteggi.
            Se invece il cliente cambia solo veicolo usa «Sostituisci mezzo», se si ferma per un periodo usa «Sospendi».
          </p>
          <div className="space-y-2">
            <Label>Motivo *</Label>
            <Select value={data.reason_code} onValueChange={(v) => setData({ ...data, reason_code: v })}>
              <SelectTrigger data-testid="close-reason-select"><SelectValue placeholder="Scegli il motivo" /></SelectTrigger>
              <SelectContent>
                {MOTIVI_CHIUSURA.map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="closure_date">Data di chiusura *</Label>
            <Input id="closure_date" type="date" value={data.closure_date}
              onChange={(e) => setData({ ...data, closure_date: e.target.value })} data-testid="close-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="refund_amount">Rimborso premio non goduto (€, se c'è)</Label>
            <Input id="refund_amount" inputMode="decimal" value={data.refund_amount}
              onChange={(e) => setData({ ...data, refund_amount: e.target.value })} placeholder="Es: 120,50" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="closure_notes">Note</Label>
            <Textarea id="closure_notes" rows={2} value={data.notes}
              onChange={(e) => setData({ ...data, notes: e.target.value })}
              placeholder="Es: venduta il 15/10 a privato, atto di vendita in documenti" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={conferma} disabled={saving} className="bg-gray-700 hover:bg-gray-800" data-testid="confirm-close-btn">
            <XCircle className="h-4 w-4 mr-2" />
            Chiudi polizza
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClosePolicyDialog;
